import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowUp, ChevronDown, Loader2, Settings2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { CohortBoard, type CohortBoardPayload } from '@/components/CohortBoard';
import { CompanyMapBoard, isCompanyMapBoard } from '@/components/CompanyMapBoard';
import { ResearchLedger, isResearchLedger } from '@/components/ResearchLedger';
import { SecGraphBoard, isSecGraphBoard } from '@/components/SecGraphBoard';
import { merlinService } from '@/services/api';
import { MERLIN_USE_VIC_CHAT } from '@/config/features';

export type MerlinMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  jobId?: string;
  status?: string;
  kind?: string;
  board?: CohortBoardPayload;
};

type SessionChangePatch = {
  messages?: MerlinMessage[];
  sessionId?: string | null;
  jobKind?: string;
  ticker?: string;
  asOf?: string;
};

type Props = {
  initialMessages?: MerlinMessage[];
  initialSessionId?: string | null;
  initialJobKind?: string;
  initialTicker?: string;
  initialAsOf?: string;
  onSessionChange?: (patch: SessionChangePatch) => void;
};

const SUGGESTED_PROMPTS = MERLIN_USE_VIC_CHAT
  ? [
      'How much revenue will SPCX have next quarter',
      'Tell me about healthcare',
      'Map SPCX',
      'SEC graph AAPL',
      'SPCX',
      'Show latest SPCX desk',
      'ATR — does Narcan destock recover in 2027?',
      'Assemble the desk on ATR — does destock recover in 2027?',
      'Hunt a name as of the latest tape',
    ]
  : [
      'What moved markets this week?',
      'Summarize the latest major stories',
      'Which sectors are leading right now?',
      'Explain the GLP-1 storyline impact on stocks',
    ];

const WELCOME_MESSAGE: MerlinMessage = {
  id: 'welcome',
  role: 'assistant',
  content: MERLIN_USE_VIC_CHAT
    ? "I'm Merlin. Ask a real question and I will research it in the warehouse — you can watch the ledger fill as files are kept, dropped, or chased. Maps, asks, theses, hunts, and the style desk are still here if you name them."
    : "I'm Merlin — Guidance Gap's research assistant. Ask me about market narratives, sector themes, stock storylines, or what's driving price action.",
};

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 py-1">
      {[0, 0.2, 0.4].map((delay) => (
        <motion.div
          key={delay}
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.4, repeat: Infinity, delay }}
          className="h-1.5 w-1.5 rounded-full bg-gray-400"
        />
      ))}
    </div>
  );
}

const MARKDOWN_COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1 className="mb-2 mt-4 text-xl font-semibold tracking-tight text-gray-900 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-4 text-lg font-semibold tracking-tight text-gray-900">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1.5 mt-3 text-base font-semibold text-gray-900">{children}</h3>
  ),
  p: ({ children }) => <p className="mb-3 last:mb-0 leading-7 text-gray-800">{children}</p>,
  ul: ({ children }) => (
    <ul className="mb-3 list-disc space-y-1.5 pl-6 leading-7 text-gray-800">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-3 list-decimal space-y-1.5 pl-6 leading-7 text-gray-800">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-2 border-gray-300 pl-4 text-gray-700">{children}</blockquote>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-blue-600 underline decoration-blue-200 underline-offset-2 hover:text-blue-800"
    >
      {children}
    </a>
  ),
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <code className="block overflow-x-auto rounded-xl bg-gray-900 px-4 py-3 text-[13px] text-gray-100">
          {children}
        </code>
      );
    }
    return (
      <code className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[13px] text-gray-800">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-3 overflow-x-auto rounded-xl bg-gray-900 p-4 text-[13px] text-gray-100">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto rounded-xl border border-gray-200">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-gray-100 px-3 py-2 text-gray-800">{children}</td>
  ),
};

function ChatMarkdown({ content }: { content: string }) {
  return (
    <div className="text-[15px]">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

const WAREHOUSE_MEMO_HEADING = '## Warehouse findings';

function mapMemoMarkdown(board: CohortBoardPayload): string {
  const memo = (board.memo || '').trim();
  const drill = (board.drill || '').trim();
  if (!drill) return memo;
  if (memo.includes(WAREHOUSE_MEMO_HEADING)) return memo;
  return [memo, WAREHOUSE_MEMO_HEADING, drill].filter(Boolean).join('\n\n');
}

function MapMemoSlot({ board }: { board: CohortBoardPayload }) {
  const markdown = mapMemoMarkdown(board);
  if (!markdown) return null;
  return (
    <details className="rounded-2xl border border-gray-200 px-4 py-2" open={Boolean(board.drill)}>
      <summary className="cursor-pointer text-sm font-medium text-gray-700">Memo</summary>
      <div className="mt-3">
        <ChatMarkdown content={markdown} />
      </div>
    </details>
  );
}

function MessageRow({
  message,
  onInvestigate,
}: {
  message: MerlinMessage;
  onInvestigate?: (ask: string) => void;
}) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="flex justify-end py-2"
      >
        <div className="max-w-[min(85%,42rem)] rounded-[1.75rem] bg-gray-100 px-5 py-2.5 text-[15px] leading-relaxed text-gray-900">
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="py-3"
    >
      {message.status && message.status !== 'completed' ? (
        <p className="mb-2 text-xs text-gray-400">{message.status}</p>
      ) : null}
      {message.board ? (
        isSecGraphBoard(message.board) || message.kind === 'sec' ? (
          <SecGraphBoard
            board={message.board}
            memoSlot={
              message.board.memo ? (
                <details className="rounded-2xl border border-gray-200 px-4 py-2">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700">Memo</summary>
                  <div className="mt-3">
                    <ChatMarkdown content={message.board.memo} />
                  </div>
                </details>
              ) : null
            }
          />
        ) : isResearchLedger(message.board) ? (
          <ResearchLedger
            board={message.board}
            status={message.status}
            memoSlot={
              message.board.memo ? (
                <div>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Answer
                  </h3>
                  <ChatMarkdown content={message.board.memo} />
                </div>
              ) : message.status === 'queued' || message.status === 'running' ? (
                <p className="text-sm text-gray-500">{message.content}</p>
              ) : null
            }
          />
        ) : isCompanyMapBoard(message.board) || message.kind === 'map' ? (
          <CompanyMapBoard
            board={message.board}
            onInvestigate={onInvestigate}
            memoSlot={<MapMemoSlot board={message.board} />}
          />
        ) : (
          <CohortBoard
            board={message.board}
            memoSlot={
              message.board.memo ? (
                <details className="rounded-2xl border border-gray-200 px-4 py-2">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700">Memo</summary>
                  <div className="mt-3">
                    <ChatMarkdown content={message.board.memo} />
                  </div>
                </details>
              ) : null
            }
          />
        )
      ) : (
        <ChatMarkdown content={message.content} />
      )}
    </motion.div>
  );
}

export function MerlinChat({
  initialMessages = [],
  initialSessionId = null,
  initialJobKind = 'auto',
  initialTicker = '',
  initialAsOf = '',
  onSessionChange,
}: Props) {
  const [messages, setMessages] = useState<MerlinMessage[]>(() =>
    initialMessages.length > 0 ? initialMessages : [WELCOME_MESSAGE],
  );
  const [input, setInput] = useState('');
  const runningJobId =
    initialMessages.find(
      (message) =>
        Boolean(message.jobId) && (message.status === 'queued' || message.status === 'running'),
    )?.jobId ?? null;
  const [isLoading, setIsLoading] = useState(Boolean(runningJobId));
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId);
  const [jobKind, setJobKind] = useState(initialJobKind);
  const [ticker, setTicker] = useState(initialTicker);
  const [asOf, setAsOf] = useState(initialAsOf);
  const [activeJobId, setActiveJobId] = useState<string | null>(runningJobId);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const syncSession = useCallback(
    (patch: SessionChangePatch) => {
      onSessionChange?.(patch);
    },
    [onSessionChange],
  );
  const syncSessionRef = useRef(syncSession);
  syncSessionRef.current = syncSession;

  useEffect(() => {
    if (!MERLIN_USE_VIC_CHAT) return;
    const stale = messages.find(
      (message) =>
        message.board &&
        (message.kind === 'map' || isCompanyMapBoard(message.board)) &&
        !message.board.drill,
    );
    const tickerToLoad = stale?.board?.object;
    if (!tickerToLoad) return;
    let cancelled = false;
    void merlinService.sendMessage(`Map ${tickerToLoad}`).then((response) => {
      if (cancelled || !response.board?.drill) return;
      setMessages((prev) => {
        const next = prev.map((message) =>
          message.board &&
          (message.kind === 'map' || isCompanyMapBoard(message.board)) &&
          message.board.object === tickerToLoad
            ? { ...message, board: response.board ?? undefined }
            : message,
        );
        syncSessionRef.current({ messages: next });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const last = [...messages].reverse().find((message) => message.id !== 'welcome');
    if (last?.board) {
      const boardEl =
        el.querySelector('[data-company-map-board]') ?? el.querySelector('[data-cohort-board]');
      if (boardEl) {
        boardEl.scrollIntoView({ block: 'start' });
        return;
      }
    }
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  useEffect(() => {
    if (!MERLIN_USE_VIC_CHAT) return;
    void merlinService
      .getVicMeta()
      .then((meta) => {
        if (meta.default_as_of) {
          setAsOf((current) => {
            const next = current || meta.default_as_of || '';
            if (next !== current) syncSession({ asOf: next });
            return next;
          });
        }
      })
      .catch(() => {
        // Backend down — send will surface the error.
      });
  }, [syncSession]);

  useEffect(() => {
    if (!activeJobId || !MERLIN_USE_VIC_CHAT) return;
    let cancelled = false;
    let misses = 0;

    const poll = async () => {
      while (!cancelled) {
        try {
          const job = await merlinService.getVicJob(activeJobId);
          if (cancelled) return;
          misses = 0;
          setError(null);
          setMessages((prev) => {
            const next = prev.map((message) => {
              if (message.jobId !== activeJobId) return message;
              const body =
                job.status === 'failed'
                  ? `Failed: ${job.error || 'unknown error'}`
                  : job.markdown || message.content;
              return {
                ...message,
                content: body,
                status: job.status,
                board: job.board ?? message.board,
              };
            });
            syncSessionRef.current({ messages: next });
            return next;
          });
          if (job.status === 'completed' || job.status === 'failed') {
            setActiveJobId(null);
            setIsLoading(false);
            return;
          }
        } catch (err) {
          if (cancelled) return;
          const message = err instanceof Error ? err.message : '';
          const offline = /failed to fetch|networkerror|load failed|network request failed/i.test(
            message,
          );
          const missing = /unknown job|404/i.test(message);
          if (missing) {
            setError(
              'The server reloaded and lost this job. Open a new chat and send the request again.',
            );
            setActiveJobId(null);
            setIsLoading(false);
            return;
          }
          if (offline) {
            misses += 1;
            if (misses === 1) {
              setError('Backend unreachable — retrying. The warehouse run may still be in progress.');
            }
            await new Promise((resolve) => setTimeout(resolve, Math.min(8000, 1500 * misses)));
            continue;
          }
          setError(message || 'Could not poll the research job.');
          setActiveJobId(null);
          setIsLoading(false);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 2500));
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
  }, [activeJobId]);

  const sendMessage = useCallback(
    async (text: string, opts?: { job?: string }) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      const userMessage: MerlinMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmed,
      };

      const history = [...messages, userMessage];
      setMessages(history);
      syncSession({ messages: history });
      setInput('');
      setError(null);
      setIsLoading(true);

      try {
        const response = await merlinService.sendMessage(
          trimmed,
          history
            .filter((m) => m.id !== 'welcome')
            .map((m) => ({ role: m.role, content: m.content })),
          MERLIN_USE_VIC_CHAT
            ? {
                sessionId,
                job: opts?.job ?? jobKind,
                ticker: ticker.trim().toUpperCase(),
                asOf: asOf.trim(),
              }
            : {},
        );

        if (response.sessionId) {
          setSessionId(response.sessionId);
          syncSession({ sessionId: response.sessionId });
        }
        if (response.ticker) {
          const nextTicker = response.ticker;
          setTicker(nextTicker);
          syncSession({ ticker: nextTicker });
        }

        const nextMessages: MerlinMessage[] = [
          ...history,
          {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: response.reply,
            jobId: response.jobId || undefined,
            status: response.jobStatus || undefined,
            kind: response.kind || undefined,
            board: response.board || undefined,
          },
        ];
        setMessages(nextMessages);
        syncSession({ messages: nextMessages });

        if (response.jobId) {
          setActiveJobId(response.jobId);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Something went wrong. Please try again.';
        setError(message);
        setIsLoading(false);
      } finally {
        inputRef.current?.focus();
      }
    },
    [asOf, isLoading, jobKind, messages, sessionId, syncSession, ticker],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  const hasUserMessages = messages.some((m) => m.role === 'user');
  const showEmptyState = !hasUserMessages && !isLoading;
  const busy = isLoading || Boolean(activeJobId);
  const threadWidth = messages.some((m) => m.board) ? 'max-w-6xl' : 'max-w-3xl';

  const footer = useMemo(
    () =>
      MERLIN_USE_VIC_CHAT
        ? 'Merlin can make mistakes. Warehouse runs may take several minutes.'
        : 'Merlin can make mistakes. Verify important decisions independently.',
    [],
  );

  const jobLabel =
    jobKind === 'retrieve'
      ? 'Open saved'
      : jobKind === 'map'
        ? 'Company map'
        : jobKind === 'sec'
          ? 'SEC graph'
          : jobKind === 'cohort'
          ? 'Style desk'
          : jobKind === 'research'
            ? 'Research'
            : jobKind.charAt(0).toUpperCase() + jobKind.slice(1);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {showEmptyState ? (
          <div className="flex h-full flex-col items-center justify-center px-4 pb-8">
            <h2 className="mb-8 text-2xl font-medium text-gray-900">What can I help with?</h2>
          </div>
        ) : (
          <div className={`mx-auto w-full ${threadWidth} px-4 py-6 md:px-8`}>
            <AnimatePresence initial={false}>
              {messages
                .filter((message) => message.id !== 'welcome')
                .map((message) => (
                  <MessageRow
                    key={message.id}
                    message={message}
                    onInvestigate={(ask) => void sendMessage(ask, { job: 'ask' })}
                  />
                ))}
            </AnimatePresence>

            {busy && !activeJobId ? (
              <div className="py-3">
                <TypingDots />
              </div>
            ) : null}

            {error ? <p className="py-2 text-center text-sm text-rose-600">{error}</p> : null}
          </div>
        )}
      </div>

      <div className="shrink-0 bg-gradient-to-t from-white via-white to-white/80 px-4 pb-4 pt-2 md:px-8">
        {showEmptyState ? (
          <div className="mx-auto mb-4 grid w-full max-w-3xl grid-cols-1 gap-2 sm:grid-cols-2">
            {SUGGESTED_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => void sendMessage(prompt)}
                className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                {prompt}
              </button>
            ))}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className={`mx-auto w-full ${threadWidth}`}>
          {MERLIN_USE_VIC_CHAT && settingsOpen ? (
            <div className="mb-2 flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
              <label className="flex items-center gap-2 text-xs text-gray-500">
                Job
                <select
                  value={jobKind}
                  onChange={(e) => {
                    setJobKind(e.target.value);
                    syncSession({ jobKind: e.target.value });
                  }}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-gray-400 focus:outline-none"
                >
                  <option value="auto">auto</option>
                  <option value="research">research</option>
                  <option value="ask">ask</option>
                  <option value="map">company map</option>
                  <option value="sec">SEC graph</option>
                  <option value="thesis">thesis</option>
                  <option value="hunt">hunt</option>
                  <option value="cohort">style desk</option>
                  <option value="sleeve">sleeve</option>
                  <option value="retrieve">open saved</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-500">
                Ticker
                <input
                  value={ticker}
                  onChange={(e) => {
                    const next = e.target.value.toUpperCase();
                    setTicker(next);
                    syncSession({ ticker: next });
                  }}
                  placeholder="ATR"
                  maxLength={5}
                  className="w-20 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-gray-400 focus:outline-none"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-500">
                As of
                <input
                  value={asOf}
                  onChange={(e) => {
                    setAsOf(e.target.value);
                    syncSession({ asOf: e.target.value });
                  }}
                  placeholder="YYYY-MM-DD"
                  className="w-32 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-gray-400 focus:outline-none"
                />
              </label>
            </div>
          ) : null}

          <div className="relative flex items-end rounded-[1.75rem] border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)] focus-within:border-gray-300">
            {MERLIN_USE_VIC_CHAT ? (
              <button
                type="button"
                onClick={() => setSettingsOpen((open) => !open)}
                className="ml-2 mb-2 flex h-9 items-center gap-1 rounded-full px-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
                aria-label="Research settings"
              >
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">{jobLabel}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            ) : null}

            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Merlin"
              rows={1}
              disabled={busy}
              className="max-h-40 min-h-[52px] flex-1 resize-none bg-transparent px-2 py-3.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:opacity-60"
            />

            <button
              type="submit"
              disabled={!input.trim() || busy}
              className="mb-2 mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              aria-label="Send message"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </button>
          </div>

          <p className="mt-2 text-center text-xs text-gray-400">{footer}</p>
        </form>
      </div>
    </div>
  );
}
